/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Overview Access Table
 *
 * Migrated from: lego/page/eos/overview/StatusDocuments/EosOverviewAccessTable/EosOverviewAccessTable.jsx
 *
 * 9-column table: Trial, Country, Site, WF#, Phase, Assigned To / Approver, Last Access, User Status, Locked/Unlocked
 *
 * Reuses:
 *   - WorkflowDetailsModal (~/components/modals/WorkflowDetailsModal)
 *   - DateCell (~/components/shared/DateCell)
 */

import { useTranslation } from 'react-i18next'
import { useModal } from '../../../../contexts/ModalContext'
import WorkflowDetailsModal from '../../../../components/modals/WorkflowDetailsModal'
import DateCell from '../../../../components/shared/DateCell'
import type { EosAccessTableProps } from '../types'

const EosOverviewAccessTable: React.FC<EosAccessTableProps> = ({ filesList, wasFiltered, isLegacy }) => {
  const { t } = useTranslation()
  const { showModal } = useModal()

  return (
    <table className="styled-table chita-table mb0 EosOverviewAccessTable">
      <thead>
        <tr>
          <th>{t('EMTF_MD_TRIAL')}</th>
          <th>{t('EMTF_MD_COUNTRY')}</th>
          <th>{t('EMTF_MD_SITE')}</th>
          <th>{t('REPORTS_TABLE_TH_WF_NO')}</th>
          <th>{t('CURRENT_PHASE')}</th>
          {!isLegacy && <th>{t('ASSIGNED_TO')}</th>}
          {isLegacy && <th>{t('TABLE_TH_APPROVER')}</th>}
          <th>{t('REPORTS_TABLE_TH_LAST_ACCESS')}</th>
          <th>{t('TABLE_TH_USER_STATUS')}</th>
          <th>{t('REPORTS_TABLE_TH_LOCKED_UNLOCKED')}</th>
        </tr>
      </thead>

      {(!filesList || filesList.length === 0) && (
        <tbody>
          <tr className="no-results">
            <td colSpan={9}>{t('NO_RESULTS_FILTERED')}</td>
          </tr>
        </tbody>
      )}

      <tbody>
        {filesList?.map((file, index) => (
          <tr key={index}>
            <td>{file.studyName ?? t('NOT_AVAILABLE')}</td>
            <td>{file.country ?? t('NOT_AVAILABLE')}</td>
            <td>{file.siteFolderName ?? t('NOT_AVAILABLE')}</td>
            <td>
              {file.userAccess?.workflowFormattedId ? (
                <a
                  className="text-link"
                  onClick={() => showModal(<WorkflowDetailsModal wfId={file.userAccess!.workflowId!} />)}
                >
                  {file.userAccess.workflowFormattedId}
                </a>
              ) : (
                <span>{t('NOT_AVAILABLE')}</span>
              )}
            </td>
            <td>{file.activeWorkflowStep ?? <span>{t('NOT_AVAILABLE')}</span>}</td>
            <td>{file.userAccess?.approver?.fullName ?? <span>{t('NOT_AVAILABLE')}</span>}</td>
            <td>
              {file.userAccess?.approver?.lastAccessAt ? (
                <DateCell val={file.userAccess.approver.lastAccessAt} />
              ) : (
                <span>{t('NOT_AVAILABLE')}</span>
              )}
            </td>
            <td>
              {file.userAccess?.approver ? (
                <span>{t(file.userAccess.approver.enabled ? 'ACTIVE' : 'INACTIVE')}</span>
              ) : (
                <span>{t('NOT_AVAILABLE')}</span>
              )}
            </td>
            <td>
              {file.userAccess?.approver ? (
                <span>{t(file.userAccess.approver.locked ? 'USER_STATUS_LOCKED' : 'USER_STATUS_UNLOCKED')}</span>
              ) : (
                <span>{t('NOT_AVAILABLE')}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default EosOverviewAccessTable
